import {
    CompanyLookup,
    SignupCompany,
    SignupCountry,
    SignupFieldPath,
    signupFieldPaths,
    SignupHubUser,
    SignupRequest,
} from '../../types/merchantSignup/MerchantSignup';
import {
    getPostalCodeExample,
    isEmailValid,
    isFullNameValid,
    isPhoneValid,
    isPostalCodeValid,
    isWebsiteValid,
    normalizeOrganizationNumber,
    normalizePhone,
    normalizePostalCode,
    normalizeWebsite,
} from '../../services/helpers/merchantSignup';

/**
 * The values of the signup form: the country and organization number the company is looked up by, then the company
 * and hub user shaped like the request.
 */
export interface SignupFormValues {
    country: SignupCountry;
    organizationNumber: string;
    company: SignupCompany;
    hubUser: SignupHubUser;
}

/** The form as it starts: the country and organization number (e.g. from a prefilled link), everything else empty. */
export const initialValuesOf = ({
    country,
    organizationNumber,
}: Pick<SignupFormValues, 'country' | 'organizationNumber'>): SignupFormValues => ({
    country,
    organizationNumber,
    company: {
        name: '',
        address: { street: '', postalCode: '', city: '' },
        website: '',
        phone: '',
        email: '',
    },
    hubUser: { name: '', phone: '', email: '' },
});

/** The company details prefilled from the lookup, empty where the registry lacks them. */
export const companyOf = (lookup: CompanyLookup): SignupCompany => ({
    name: lookup.companyName ?? '',
    address: {
        street: lookup.address?.street ?? '',
        postalCode: lookup.address?.postalCode ?? '',
        city: lookup.address?.city ?? '',
    },
    website: lookup.website ?? '',
    phone: lookup.phone ?? '',
    email: lookup.email ?? '',
});

/** The message for a field that is not valid, in the words of the hint under the button. Undefined when valid. */
export const getFieldError = (
    country: SignupCountry,
    path: SignupFieldPath,
    value: string
): string | undefined => {
    switch (path) {
        case 'company.name':
            return value.trim() ? undefined : 'Add the company name.';
        case 'company.address.street':
            return value.trim() ? undefined : 'Add a street address.';
        case 'company.address.postalCode':
            return isPostalCodeValid(country, value)
                ? undefined
                : `Enter a valid postal code, e.g. ${getPostalCodeExample(country)}.`;
        case 'company.address.city':
            return value.trim() ? undefined : 'Add a city.';
        case 'company.website':
            return isWebsiteValid(value)
                ? undefined
                : 'Enter your website, e.g. www.yourstore.com.';
        case 'company.phone':
            return isPhoneValid(value) ? undefined : 'Enter a valid company phone number.';
        case 'company.email':
            return isEmailValid(value) ? undefined : 'Enter a valid company email address.';
        case 'hubUser.name':
            return isFullNameValid(value) ? undefined : 'Add your full name.';
        case 'hubUser.phone':
            return isPhoneValid(value) ? undefined : 'Enter a valid phone number.';
        case 'hubUser.email':
            return isEmailValid(value) ? undefined : 'Enter a valid email address.';
        default:
            return undefined;
    }
};

/**
 * The first problem in field order, as the hint under the button names it. Undefined when everything is valid.
 * values are the field values in signupFieldPaths.
 */
export const getFirstProblem = (country: SignupCountry, values: string[]): string | undefined =>
    signupFieldPaths
        .map((path, index) => getFieldError(country, path, values[index]))
        .find(error => error);

/**
 * The signup as the API wants it: the organization number as digits, the company details as edited, website on
 * https://, postal code without spaces, phone numbers with the calling code of the country.
 */
export const toSignupRequest = (
    { country, organizationNumber, company, hubUser }: SignupFormValues,
    key?: string
): SignupRequest => ({
    countryCode: country,
    organizationNumber: normalizeOrganizationNumber(organizationNumber),
    company: {
        name: company.name.trim(),
        address: {
            street: company.address.street.trim(),
            postalCode: normalizePostalCode(company.address.postalCode),
            city: company.address.city.trim(),
        },
        website: normalizeWebsite(company.website),
        phone: normalizePhone(country, company.phone),
        email: company.email.trim(),
    },
    hubUser: {
        name: hubUser.name.trim(),
        phone: normalizePhone(country, hubUser.phone),
        email: hubUser.email.trim(),
    },
    ...(key ? { key } : {}),
});
